import { useState } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import {
  RECOMMENDED_ACTIVITIES_CATEGORIES,
  type RecommendedActivitiesCategory,
} from '@domain/features/suggested-activities/suggested-activities-categories'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useRecommendedActivities, type RecommendedActivityTile } from '@presentation/hooks/useRecommendedActivities'
import RecommendedActivityCard from './RecommendedActivityCard'
import RecommendedActivityDetailsModal from './RecommendedActivityDetailsModal'
import RecommendedActivitiesSignupModal from './RecommendedActivitiesSignupModal'

function buildPlaceholderTiles(): RecommendedActivityTile[] {
  return RECOMMENDED_ACTIVITIES_CATEGORIES.map(
    (category: RecommendedActivitiesCategory) => ({
      category,
      content: null,
    }),
  )
}

export default function RecommendedActivitiesSection() {
  const { tiles, loading, error } = useRecommendedActivities()
  const visibleTiles = tiles.length > 0 ? tiles : buildPlaceholderTiles()
  const [selectedTile, setSelectedTile] = useState<RecommendedActivityTile | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false)

  function handleCardClick(tile: RecommendedActivityTile) {
    setSelectedTile(tile)
    setIsDetailsModalOpen(true)
  }

  function handleViewAllClick() {
    setIsSignupModalOpen(true)
  }

  function handleDetailsModalClose() {
    setIsDetailsModalOpen(false)
    setSelectedTile(null)
  }

  function handleViewMoreActivitiesClick() {
    setIsDetailsModalOpen(false)
    setSelectedTile(null)
    setIsSignupModalOpen(true)
  }

  return (
    <>
      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto flex max-w-7xl flex-col pl-8 pr-4 sm:pl-10 sm:pr-6 lg:pl-14 lg:pr-8">
          <div className="pl-2 sm:pl-4 lg:pl-5">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold tracking-tight text-blue sm:text-3xl">
                Recommended Activities
              </h2>
              <button
                type="button"
                onClick={handleViewAllClick}
                aria-label="View all recommended Parkinson's activities"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue px-3 py-2 text-sm font-semibold text-blue transition-colors hover:border-blue hover:bg-blue/8"
              >
                View all
                <FiExternalLink className="size-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 h-0.5 w-full bg-blue" />
          </div>

          {loading && (
            <div className="mt-6">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <p className="mt-4 text-sm text-text-secondary">
              Some recommendations could not be loaded right now.
            </p>
          )}

          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {visibleTiles.map((tile) => (
              <RecommendedActivityCard
                key={tile.category.key}
                tile={tile}
                onClick={handleCardClick}
              />
            ))}
          </div>
        </div>
      </section>

      <RecommendedActivityDetailsModal
        isOpen={isDetailsModalOpen}
        tile={selectedTile}
        onClose={handleDetailsModalClose}
        onViewMoreActivities={handleViewMoreActivitiesClick}
      />

      <RecommendedActivitiesSignupModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
      />
    </>
  )
}
